import useDebouncedCallback from 'hooks/useDebouncedCallback';
import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import { useHistory } from 'react-router';
import Layout from '../../Components/layouts';
import GenericTable from '../../Components/GenericTable';
import { CoursesServices } from '../../Services/CoursesServices';
import { Paths } from 'Routes/routePaths';
import OutlinedButton from 'Components/OutlinedButton';
import SearchBoxInput from 'Components/SearchBoxInput';
import commonStyles from 'theme/commonStyles';
import { notify } from '../../utils';
import { DEFAULT_COURSE_IMAGE_URL } from '../../utils/constants';
import {
    formatCourseCreatedDate,
    getCourseDescription,
    getCourseName,
    getCourseThumbnail,
    truncateCourseUrl,
    withCourseImageCacheBust,
} from './courseHelpers';

const coursesServices = new CoursesServices();

const useStyles = makeStyles((theme) => ({
    pageTitle: {
        marginBottom: theme.spacing(3),
        wordBreak: 'break-word',
    },
    courseThumb: {
        display: 'block',
        width: 72,
        height: 48,
        margin: '0 auto',
        borderRadius: 6,
        objectFit: 'cover',
        backgroundColor: 'var(--dm-row-alt, #f4f6f8)',
    },
    nameLink: {
        display: 'inline-block',
        maxWidth: 220,
        fontWeight: 700,
        textAlign: 'left',
        wordBreak: 'break-word',
    },
    descriptionExcerpt: {
        display: 'block',
        maxWidth: 220,
        marginTop: 4,
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: 1.35,
        textAlign: 'left',
        cursor: 'help',
    },
    stackedValue: {
        minWidth: 150,
        textAlign: 'left',
        '& span': {
            display: 'block',
        },
        '& span + span': {
            marginTop: 3,
            color: 'var(--dm-muted, #647184)',
            fontSize: '0.75rem',
        },
    },
    linkCell: {
        minWidth: 150,
        textAlign: 'left',
    },
    launchLink: {
        fontWeight: 700,
    },
    linkUrl: {
        display: 'block',
        marginTop: 3,
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.7rem',
        fontWeight: 400,
    },
    providerFilter: {
        minWidth: 190,
        width: 220,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
}));

const isMoodleCourse = (course) => (
    course.is_moodle === true || course.is_moodle === 1 || course.is_moodle === 'true'
);

const getColumns = (classes, handleItemClick) => [
    {
        label: 'Thumb',
        key: 'thumbnail',
        render: (course) => {
            const thumbnail = getCourseThumbnail(course) || DEFAULT_COURSE_IMAGE_URL;
            const thumbnailSrc = withCourseImageCacheBust(
                thumbnail,
                course.updated_at || course.last_update_on,
            );
            return (
                <img
                    className={classes.courseThumb}
                    src={thumbnailSrc}
                    alt={`${getCourseName(course) || 'Course'} thumbnail`}
                    loading="lazy"
                    decoding="async"
                />
            );
        },
    },
    {
        label: 'Name',
        key: 'name',
        render: (course) => {
            const description = getCourseDescription(course);
            const excerpt = description.length > 92 ? `${description.slice(0, 91)}…` : description;
            return (
                <div>
                    <Link
                        component="button"
                        type="button"
                        className={classes.nameLink}
                        onClick={() => handleItemClick(course)}
                    >
                        {getCourseName(course) || '—'}
                    </Link>
                    {description ? (
                        <Tooltip title={description} placement="top" arrow>
                            <span className={classes.descriptionExcerpt}>{excerpt}</span>
                        </Tooltip>
                    ) : null}
                </div>
            );
        },
    },
    { label: 'Type', key: 'course_type' },
    { label: 'Provider', key: 'provider' },
    {
        label: 'Category / Domain',
        key: 'category',
        render: (course) => (
            <div className={classes.stackedValue}>
                <span>{course.category || '—'}</span>
                <span>{course.domain || '—'}</span>
            </div>
        ),
    },
    {
        label: 'Duration',
        key: 'duration_label',
        render: (course) => course.duration_label || course.duration || '—',
    },
    { label: 'Language', key: 'language' },
    {
        label: 'Created',
        key: 'created_at',
        render: (course) => formatCourseCreatedDate(course.created_at),
    },
    {
        label: 'Link',
        key: 'link',
        render: (course) => course.link ? (
            <div className={classes.linkCell}>
                <Link
                    className={classes.launchLink}
                    href={course.link}
                    target="_blank"
                    rel="noreferrer"
                >
                    {isMoodleCourse(course) ? 'Moodle' : 'Launch'}
                </Link>
                {!isMoodleCourse(course) ? (
                    <span className={classes.linkUrl} title={course.link}>
                        {truncateCourseUrl(course.link)}
                    </span>
                ) : null}
            </div>
        ) : '—',
    },
    { label: 'Action', key: 'action', actions: ['edit'] },
];

const Courses = () => {
    const classes = useStyles();
    const history = useHistory();
    const commonClasses = commonStyles();

    const [filterState, setFilterState] = React.useState({ page: 1, search: '', provider: '' });
    const [courseList, setCourseList] = React.useState([]);
    const [providerOptions, setProviderOptions] = React.useState([]);
    const [pagination, setPagination] = React.useState(null);
    const [loading, setLoading] = React.useState(true);

    const getAllCourses = async (queryData) => {
        const providerQuery = queryData.provider
            ? `&provider=${encodeURIComponent(queryData.provider)}`
            : '';
        const query = `search=${encodeURIComponent(queryData.search || '')}&page=${queryData.page}${providerQuery}`;
        setLoading(true);
        try {
            const response = await coursesServices.getAllCourses(`main/courselist?${query}`);
            const results = response && Array.isArray(response.results) ? response.results : [];
            const providers = response
                && response.filters
                && Array.isArray(response.filters.providers)
                ? response.filters.providers
                : [];
            setCourseList(results);
            setProviderOptions(providers);
            setPagination(response && response.pagination ? response.pagination : null);
        } catch (error) {
            setCourseList([]);
            setPagination(null);
            notify('error', 'Unable to load courses');
        } finally {
            setLoading(false);
        }
    };

    const searchLater = useDebouncedCallback(() => {
        setFilterState(current => ({ ...current, page: 1 }));
        getAllCourses({ ...filterState, page: 1 });
    }, 800);

    const handlePageChange = (evt, page) => {
        searchLater.cancel();
        const nextFilter = { ...filterState, page };
        setFilterState(nextFilter);
        return getAllCourses(nextFilter);
    };

    const handleSearch = (event) => {
        event.preventDefault();
        searchLater.cancel();
        const nextFilter = { ...filterState, page: 1 };
        setFilterState(nextFilter);
        return getAllCourses(nextFilter);
    };

    const handleProviderChange = (event) => {
        searchLater.cancel();
        const nextFilter = {
            ...filterState,
            page: 1,
            provider: event.target.value,
        };
        setFilterState(nextFilter);
        return getAllCourses(nextFilter);
    };

    const handleItemClick = (row) => {
        if (!row) return null;
        history.push(`${Paths.AddCourses}/${row.uuid}`);
        return null;
    };

    const handleCreateNew = () => history.push(Paths.AddCourses);
    const columns = getColumns(classes, handleItemClick);

    React.useEffect(() => {
        searchLater();
        return searchLater.cancel;
    }, [filterState.search, searchLater]);

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <Typography variant="h4" className={commonClasses.pageTitle}>Courses</Typography>
                    <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                        <Grid item xs={12} sm={6} md={4}>
                            <SearchBoxInput
                                placeholder="Search courses"
                                value={filterState.search}
                                handleChange={(event) => setFilterState({
                                    ...filterState,
                                    page: 1,
                                    search: event.target.value,
                                })}
                                handleSearch={handleSearch}
                            />
                        </Grid>
                        <Grid
                            item
                            container
                            xs={12}
                            sm={6}
                            md={8}
                            spacing={2}
                            alignItems="center"
                            justifyContent="flex-end"
                        >
                            <Grid item xs={12} sm="auto">
                                <TextField
                                    select
                                    className={classes.providerFilter}
                                    variant="outlined"
                                    size="small"
                                    label="Provider"
                                    name="provider-filter"
                                    value={filterState.provider}
                                    onChange={handleProviderChange}
                                    SelectProps={{ native: true }}
                                    InputLabelProps={{ shrink: true }}
                                >
                                    <option value="">All providers</option>
                                    {providerOptions.map((provider) => (
                                        <option key={provider.value} value={provider.value}>
                                            {provider.label}
                                        </option>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm="auto">
                                <OutlinedButton
                                    title="Create Course"
                                    handleClick={handleCreateNew}
                                    align="right"
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid container style={{ marginTop: 20 }}>
                        <GenericTable
                            columns={columns}
                            loading={loading}
                            handleItemClick={handleItemClick}
                            list={courseList}
                        />
                    </Grid>
                    <Grid container style={{ marginTop: 22 }}>
                        {pagination ? (
                            <Pagination
                                page={filterState.page}
                                onChange={handlePageChange}
                                className={commonClasses.pagination}
                                count={Number(pagination.total_pages) || 1}
                                variant="outlined"
                                shape="rounded"
                                siblingCount={0}
                            />
                        ) : null}
                    </Grid>
                </div>
            </main>
        </Layout>
    );
};

export default Courses;
